import { useEffect, useState, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';

export interface SecurityMetrics {
  readonly activeSessions: number;
  readonly lockedUsers: number;
  readonly failedLoginsToday: number;
  readonly successfulLoginsToday: number;
  readonly activeDelegations: number;
  readonly activeUsers: number;
  readonly totalUsers: number;
  readonly inactiveUsers: number;
  readonly mfaFailuresToday: number;
  readonly passwordExpiryAlerts: number;
  readonly suspiciousActivityAlerts: number;
}

interface TileProps {
  readonly id: string;
  readonly label: string;
  readonly value: number;
}

function Tile({ id, label, value }: TileProps): ReactNode {
  return (
    <div role="figure" aria-label={label} data-testid={`kpi-tile-${id}`}>
      <div data-testid={`kpi-label-${id}`}>{label}</div>
      <div data-testid={`kpi-value-${id}`}>{value}</div>
    </div>
  );
}

export function SecurityDashboardPage(): ReactNode {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [metrics, setMetrics] = useState<SecurityMetrics | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    apiRequest<SecurityMetrics>('/security/dashboard/metrics', { token })
      .then((r) => { if (alive) setMetrics(r); })
      .catch((e) => { if (alive) setError((e as Error).message); });
    return () => { alive = false; };
  }, [token]);

  return (
    <main aria-label="security-dashboard" data-testid="security-dashboard-page">
      <h1>{t('admin.security.title')}</h1>
      <p>{t('admin.security.intro')}</p>
      {error ? <p role="alert">{error}</p> : null}
      {!metrics ? (
        <p>{t('admin.security.loading')}</p>
      ) : (
        <section aria-label="kpi-grid" data-testid="kpi-grid">
          <Tile id="total-users" label={t('admin.security.totalUsers')} value={metrics.totalUsers} />
          <Tile id="active-users" label={t('admin.security.activeUsers')} value={metrics.activeUsers} />
          <Tile id="inactive-users" label={t('admin.security.inactiveUsers')} value={metrics.inactiveUsers} />
          <Tile id="locked-users" label={t('admin.security.lockedUsers')} value={metrics.lockedUsers} />
          <Tile id="failed-logins-today" label={t('admin.security.failedLogins')} value={metrics.failedLoginsToday} />
          <Tile id="successful-logins-today" label={t('admin.security.successfulLogins')} value={metrics.successfulLoginsToday} />
          <Tile id="mfa-failures-today" label={t('admin.security.mfaFailures')} value={metrics.mfaFailuresToday} />
          <Tile id="password-expiry-alerts" label={t('admin.security.passwordExpiry')} value={metrics.passwordExpiryAlerts} />
          <Tile id="concurrent-sessions" label={t('admin.security.concurrentSessions')} value={metrics.activeSessions} />
          <Tile id="suspicious-activity-alerts" label={t('admin.security.suspiciousActivity')} value={metrics.suspiciousActivityAlerts} />
          <Tile id="active-delegations" label={t('admin.security.activeDelegations')} value={metrics.activeDelegations} />
        </section>
      )}
    </main>
  );
}
